import { hasDocumentSupport, hasWindowSupport } from './env';


//
// Constants
//

const w = hasWindowSupport ? window : {};
const d = hasDocumentSupport ? document : {};
const elProto = typeof Element !== 'undefined' ? Element.prototype : {};


//
// Normalization utils
//

// `requestAnimationFrame()` convenience method
// We don't have a version for cancelAnimationFrame, but we don't call it anywhere
export const requestAF = w.requestAnimationFrame || w.webkitRequestAnimationFrame || w.mozRequestAnimationFrame || w.msRequestAnimationFrame || w.oRequestAnimationFrame || ((cb) => {
    // Fallback, but not a true polyfill
    // All browsers we support (other than Opera Mini) support
    // `requestAnimationFrame()` without a polyfill
    return setTimeout(cb, 16);
});

export const MutationObs = w.MutationObserver || w.WebKitMutationObserver || w.MozMutationObserver || null;


//
// Utils
//

// Determine if an element is an HTML Element
export const isElement = (el) => { return Boolean(el && el.nodeType === Node.ELEMENT_NODE); };

// Returns true if the parent element contains the child element
export const contains = (parent, child) => {
    if (!parent || !isFunction(parent.contains)) {
        return false;
    }

    return parent.contains(child);
};

// Return the Bounding Client Rect of an element
// Returns `null` if not an element
/* istanbul ignore next: getBoundingClientRect() doesn't work in JSDOM */
export const getBCR = (el) => { return (isElement(el) ? el.getBoundingClientRect() : null); };

// Get an attribute value from an element
// Returns `null` if not found
export const getAttr = (el, attr) => { return (attr && isElement(el) ? el.getAttribute(attr) : null); };

// Determine if an attribute exists on an element
// Returns `true` or `false`, or `null` if element not found
export const hasAttr = (el, attr) => { return (attr && isElement(el) ? el.hasAttribute(attr) : null); };

// Test if an element has a class
export const hasClass = (el, className) => {
    // We are checking for `el.classList` existence here since IE 11
    // returns `undefined` for some elements (e.g. SVG elements)
    // See https://github.com/bootstrap-vue/bootstrap-vue/issues/2713
    if (className && isElement(el) && el.classList) {
        return el.classList.contains(className);
    }

    return false;
};

// Determine if an HTML element is visible - Faster than CSS check
export const isVisible = (el) => {
    if (!isElement(el) || !contains(d.body, el)) {
        return false;
    }

    if (el.style.display === 'none') {
    // We do this check to help with vue-test-utils when using v-show
    /* istanbul ignore next */
        return false;
    }

    // All browsers support getBoundingClientRect(), except JSDOM as it returns all 0's for values :(
    // So any tests that need isVisible will fail in JSDOM
    // Except when we override the getBCR prototype in some tests
    const bcr = getBCR(el);
    return Boolean(bcr && bcr.height > 0 && bcr.width > 0);
};

// Determine if an element is disabled
export const isDisabled = (el) => { return !isElement(el) || el.disabled || Boolean(getAttr(el, 'disabled')) || hasClass(el, 'disabled'); };

// Get an element given an ID
export const getById = (id) => { return d.getElementById(/^#/.test(id) ? id.slice(1) : id) || null; };

// Add a class to an element
export const addClass = (el, className) => {
    // We are checking for `el.classList` existence here since IE 11
    // returns `undefined` for some elements (e.g. SVG elements)
    // See https://github.com/bootstrap-vue/bootstrap-vue/issues/2713
    if (className && isElement(el) && el.classList) {
        el.classList.add(className);
    }
};

// Remove a class from an element
export const removeClass = (el, className) => {
    // We are checking for `el.classList` existence here since IE 11
    // returns `undefined` for some elements (e.g. SVG elements)
    // See https://github.com/bootstrap-vue/bootstrap-vue/issues/2713
    if (className && isElement(el) && el.classList) {
        el.classList.remove(className);
    }
};

// Toggle a class on an element
export const toggleClass = (el, className) => {
    if (hasClass(el, className)) {
        removeClass(el, className);
    } else {
        addClass(el, className);
    }
};

// Set an attribute on an element
export const setAttr = (el, attr, value) => {
    if (attr && isElement(el)) {
        el.setAttribute(attr, value);
    }
};

// Remove an attribute from an element
export const removeAttr = (el, attr) => {
    if (attr && isElement(el)) {
        el.removeAttribute(attr);
    }
};
